import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DataContext from "../../context/DataContext";
import InputForm from "./InputForm";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import styles from "./styles/Home.module.css";
import { cleanAndCapitalize, removeWhiteSpace } from "../../utils/utils";
import Spinner from "../../components/ui/spinner/Spinner";
import useCurrebtBU from "../../hooks/useCurrentBU";
import { fetchTickets } from "./fetchTicketsHandler";

const STATUS_TYPE = {
    0: "error",
    1: "searched ticket not found",
    2: "searched ticket rerserved",
    3: "searched ticket paid",
    4: "searched ticket found",
    5: "settlement reserved",
    6: "settlement paid",
    7: "searched ticket in settlement",
    8: "unhandled",
    9: "searched ticket in process",
};

/**
 * Home page component
 */
const Home = () => {
    const {
        setIsValidSearch, // {function} - set if valid search to true
        isValidSearch, // {Boolean} - valid search flag
        setResult, //  {Object} - The object containing the result
        setSearchedTicket, // {String} - The searched tiket number
        connection, // {Object} - The object containing the client settings

        setIsLoading, // {Function} - The function to set isLoading state
        isLoading, // {Boolean} - Is loading dtate
        reCaptchaReady, // {Boolean} - Google recaptcha script ready flag
        serviceData, // {Function} - The function to get the client service date
        isServiceDataAvailable, // {Boolean} - Service data avialabilty flag
        verifyReCaptcha, // {Function} - The function verify google recaptcha

        setResponseData, // {Function} - The function to set responceData (fetch tickets)
        setCurrentTicket, // {Function} - The function to set the current searched ticket
        handleError, // {Function} - Error handling function
        setCurrentCategory, // {Function} - The function to set the current category for photos
        homePath, //{String} - The home path adress
        paymentHeaderFooterHeight, // {Float} - The calculated height of the header and footer
        setIsServerDown,
        isServerDown,
        setTicketType,
        setIsrelatedRedirect,
        JWTToken,
    } = useContext(DataContext);

    // Get URL parameters
    const { bUser } = useParams();
    const navigate = useNavigate();

    // Manage form button disable status
    const [formButtonDisable, setFormButtonDisable] = useState(true);

    const [dateresetTrigger, setDateresetTrigger] = useState(false);

    //ticket data form inputs
    const [ticket, setTicketData] = useState({
        ticketDate: "",
        licencePlate: "",
        ticketNumber: "",
        generalTerms: false,
    });

    // Reference for ticket number input field, used to set focus on the field on page mount
    const ticketNumberRef = useRef(null);

    // Get search parameters from URL
    const [searchParams] = useSearchParams();
    const ticketNumber = searchParams.get("ticketNumber");
    const licensePlate = searchParams.get("licensePlate");

    // Set the current bUser in the context
    useCurrebtBU(bUser);

    // Set the defaults for the page
    useEffect(() => {
        setResult(null);
        setIsValidSearch(false);
        setIsServerDown(false);
        setIsrelatedRedirect(false);
    }, []);

    // Fetch service data
    useEffect(() => {
        if (bUser && JWTToken) serviceData(bUser);
    }, [bUser, JWTToken]);

    // Set ticket data if URL parameters are present
    useEffect(() => {
        if (ticketNumber && licensePlate) {
            setTicketData((prevData) => ({
                ...prevData,
                licencePlate: licensePlate,
                ticketNumber: ticketNumber,
            }));
        }
    }, [ticketNumber, licensePlate]);

    // Submit data if reCaptcha is ready and URL parameters are present
    useEffect(() => {
        if (reCaptchaReady && ticketNumber && licensePlate && connection) {
            submitData();
        }
    }, [reCaptchaReady, ticketNumber, licensePlate, ticket, connection]);

    // Set form button state based on form data and reCaptcha readiness
    useEffect(() => {
        setFormButtonDisable(
            !(
                ((ticket?.ticketNumber && ticket?.licencePlate) ||
                    (ticket?.ticketNumber && ticket?.ticketDate)) &&
                ticket?.generalTerms &&
                reCaptchaReady
            ),
        );
    }, [ticket, reCaptchaReady]);

    /**
     * Verify recaptcha and submit data if valid
     */
    const submitData = async () => {
        setIsLoading(true);

        (await verifyReCaptcha()) &&
            // (await fetchTickets(await formatUserInput()));
            (await fetchTickets(await formatUserInput(), {
                setIsLoading,
                setResponseData,
                setResult,
                setIsValidSearch,
                setCurrentTicket,
                setTicketType,
                setCurrentCategory,
                navigate,
                handleError,
                homePath,
            }));
    };

    /**
     * Format user inputs before submission
     */
    const formatUserInput = async () => {
        return {
            customerId: await connection?.uniqueId,
            ticketNumber: removeWhiteSpace(ticket?.ticketNumber),
            issueDate: ticket?.ticketDate
                ? ticket?.ticketDate
                : moment().format("YYYY-MM-DD"),
            plate: cleanAndCapitalize(ticket?.licencePlate),
        };
    };

    const formButtonDisableRef = useRef(formButtonDisable);

    useEffect(() => {
        formButtonDisableRef.current = formButtonDisable;
    }, [formButtonDisable]);

    /**
     * Submit handler
     * @param {Event} event - the event object
     */
    const handleSubmit = async (event) => {
        if (event?.preventDefault) {
            event.preventDefault();
        }
        if (!formButtonDisableRef.current) {
            setSearchedTicket(removeWhiteSpace(ticket?.ticketNumber));
            submitData();
        }
    };

    /**
     * Change handler
     * @param {Event} event - the event object
     */
    const handleChange = (event) => {
        setTicketData((prevTicketData) => ({
            ...prevTicketData,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value,
        }));
    };

    /**
     * Reset form fields and return focus to the ticket number input field
     */
    const resetFields = () => {
        setDateresetTrigger((prev) => !prev);

        setTicketData({
            ticketDate: "",
            licencePlate: "",
            ticketNumber: "",
            generalTerms: false,
        });
        ticketNumberRef.current && ticketNumberRef.current.focus();
    };
    // ---------------------------------------------------------------------------------------- Moved to outside js file for unot testing
    // /**
    //  * Fetch the searched ticket and related tickets, if available
    //  * @param {Object} userInput - the object that contains the user data input from the search form
    //  */
    // const fetchTickets = async (userInput) => {
    //     /**
    //      * check if the user input object is valid by checking if it has the required properties
    //      * @param {Object} obj the user Input object
    //      * @returns {Boolean} true if the object is valid, false otherwise
    //      */
    //     const validateUserInput = () => {
    //         return (
    //             userInput?.uniqueID &&
    //             userInput?.ticketNumber &&
    //             (userInput?.plate || userInput?.issueDate)
    //         );
    //     };

    //     setIsLoading(true);
    //     setResponseData(null);
    //     setResult(null);
    //     try {
    //         if (validateUserInput()) {
    //             const { data } = await axiosSW.post(
    //                 "Tickets/payment/GetTicketData",
    //                 userInput
    //             );

    //             setResponseData(data);

    //             if (data) {
    //                 switch (data?.outputStatus) {
    //                     case 1:
    //                     case 2:
    //                     case 3:
    //                     case 5:
    //                     case 6:
    //                     case 8:
    //                     case 9:
    //                         setResult(data);
    //                         navigate(`${homePath}/result`);
    //                         setIsLoading(false);
    //                         break;

    //                     case 4:
    //                         setIsValidSearch(true);
    //                         setCurrentTicket(data?.ticket);
    //                         setTicketType(0);
    //                         setCurrentCategory(
    //                             data?.ticket?.photos?.[0]?.category
    //                         );
    //                         navigate(`${homePath}/search`);
    //                         setIsLoading(false);
    //                         break;
    //                     case 7:
    //                         setIsValidSearch(true);
    //                         setTicketType(1);
    //                         setCurrentTicket(data?.settlements?.[0]);

    //                         setCurrentCategory(
    //                             data?.relatedTickets?.[0]?.photos?.[0]?.category
    //                         );
    //                         navigate(`${homePath}/search`);
    //                         setIsLoading(false);
    //                         break;

    //                     default:
    //                         setIsLoading(false);
    //                         handleError({
    //                             error: data,
    //                             errorMessage: "Bad response",
    //                         });
    //                 }

    //             } else {
    //                 setIsLoading(false);
    //                 handleError({
    //                     error: data,
    //                     errorMessage: "No data",
    //                 });
    //             }
    //             //  Set error to userInput and call the error handler if data is not available
    //         } else {
    //             setIsLoading(false);
    //             handleError({
    //                 error: userInput,
    //                 errorMessage: "Bad request",
    //             });
    //         }
    //     } catch (error) {
    //         setIsLoading(false);
    //         handleError(error);
    //     }
    // };
    //-----------------------------------------------------------------------------------

    return (
        <article
            className={`  main-page-container flex-container`}
            style={{
                // Set the min height of the main page container to the height of the window
                // minus the calculated height of the header and footer
                minHeight: `calc(100vh - ${paymentHeaderFooterHeight}px)`,
            }}
        >
            {(!isServerDown && !isServiceDataAvailable) || isLoading ? (
                <div className={`flex-container`}>
                    <Spinner />
                </div>
            ) : (
                <section
                    className={`${styles.main_page_container} main-container flex-container display-non`}
                >
                    {/* <DeEncryptor /> */}

                    {!isValidSearch && isServiceDataAvailable && (
                        <InputForm
                            formButtonDisable={formButtonDisable} // Form button state
                            ticket={ticket}
                            handleChange={handleChange}
                            handleSubmit={handleSubmit}
                            resetFields={resetFields}
                            ticketNumberRef={ticketNumberRef} // Reference for ticket number input
                            formButtonDisableRef={formButtonDisableRef}
                            dateresetTrigger={dateresetTrigger}
                        />
                    )}
                </section>
            )}
        </article>
    );
};

export default Home;
